import Button from "components/button";
import LoaderButton from "components/button/LoaderButton";
import TextField from "components/fields/TextField";
import Modal from "components/modal";
import React, { useEffect, useState } from "react";
import { GrNotes } from "react-icons/gr";
import { RiErrorWarningFill } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { addContainerNotes, getContainerNotes } from "store/noteSlice";

function ContainerNotes() {
  const dispatch = useDispatch();
  const { container: containerPharm = {} } = useSelector(
    (state) => state.auth.permissions || {}
  );
  const {
    containerNoteData,
    containerNoteLoading,
    containerNoteError,
    addContainerNoteLoading,
  } = useSelector((state) => state.notes);

  const {
    containerDetailsData,
    containerDetailsLoading,
    containerDetailsError,
  } = useSelector((state) => state.containers);

  const [newNote, setNewNote] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Transform notes into timeline
  const timeline = containerNoteData.map((note) => ({
    datetime: note.addedAt,
    text: note.text,
    createdBy: note.createdBy,
  }));

  // Add note handler
  const handleAddNote = async () => {
    if (!newNote.trim() || !containerDetailsData?._id) return;

    try {
      await dispatch(
        addContainerNotes({
          relatedId: containerDetailsData._id,
          relatedModel: "containers",
          text: newNote.trim(),
        })
      ).unwrap();

      // Refresh notes
      dispatch(
        getContainerNotes({
          relatedId: containerDetailsData._id,
          relatedModel: "containers",
        })
      );

      setNewNote("");
      setIsModalOpen(false);
    } catch (error) {
      console.error("Failed to add note:", error);
    }
  };

  return (
    <div className="rounded-md bg-white bg-clip-border px-2 py-2 shadow-3xl shadow-shadow-500 dark:!bg-navy-700 dark:shadow-none">
      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex flex-row items-center gap-1">
          <h4 className="text-base font-semibold text-navy-700 dark:text-white">
            Timeline
          </h4>
          <span className="inline-flex items-center gap-1 rounded-full bg-green-200 px-2 py-1 text-xs font-semibold text-green-700 shadow-sm">
            {timeline.length} Notes
          </span>
        </div>
        {containerPharm?.addNote && (
          <Button onClick={() => setIsModalOpen(true)}>Add Note</Button>
        )}
      </div>

      {/* Timeline */}
      <div className="custom-scrollbar hoverflow-y-auto relative max-h-[240px]">
        <div className="absolute left-4 top-0 h-full w-0.5 bg-gray-200 dark:bg-gray-700" />

        {containerDetailsLoading || containerNoteLoading ? (
          // 🔄 Loader state
          <div className="flex flex-col gap-2 ">
            {[...Array(4)].map((_, idx) => (
              <div key={idx} className="flex items-start gap-4">
                <div className="h-6 w-6 animate-pulse rounded-full bg-gray-200 dark:bg-navy-700" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-1/3 animate-pulse rounded-md bg-gray-200 dark:bg-navy-700"></div>
                  <div className="h-3 w-2/3 animate-pulse rounded-md bg-gray-200 dark:bg-navy-700"></div>
                </div>
              </div>
            ))}
          </div>
        ) : containerDetailsError || containerNoteError ? (
          // ❌ Error state
          <div className="flex items-center gap-2 p-4 text-red-500">
            <RiErrorWarningFill className="h-4 w-4" />
            <span>Failed to load notes</span>
          </div>
        ) : timeline.length === 0 ? (
          // ℹ️ Empty state
          <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
            No notes available.
          </div>
        ) : (
          // ✅ Notes
          <ul className="space-y-1">
            {timeline.map((entry, idx) => (
              <li key={idx} className="relative flex items-start gap-1">
                {/* Dot */}
                <span className="relative z-10 mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-500 text-white dark:bg-brand-400">
                  <GrNotes className="h-3 w-3" />
                </span>

                {/* Card */}
                <div className="w-full rounded-xl border border-gray-200 bg-white px-2 py-1 shadow-sm transition hover:shadow-md dark:border-gray-700 dark:bg-navy-800">
                  {entry.text && (
                    <p className="text-sm font-medium text-navy-700 dark:text-white">
                      {entry.text}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h5 className="mt-1 text-sm text-xs leading-relaxed text-gray-600">
                      {entry.createdBy}
                    </h5>
                    <span className="text-xs text-gray-600">
                      {entry.datetime}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        className="max-w-lg"
      >
        <h2 className="border-b py-3 pl-2 text-xl font-bold text-navy-700 dark:text-white">
          Add Note
        </h2>

        <div className="p-4">
          <TextField
            type="textarea"
            label="Write your note"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Write a note..."
            rows={4}
          />
          <div className="mt-4 flex justify-end gap-2">
            <Button onClick={() => setIsModalOpen(false)} variant="outline">
              Cancel
            </Button>

            <LoaderButton
              type="button"
              isLoading={addContainerNoteLoading}
              onClick={handleAddNote}
              disabled={!newNote.trim()}
            >
              Save Note
            </LoaderButton>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default ContainerNotes;
